// src/components/inbox/InboxPage.jsx
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useParams } from "react-router-dom";
import socket, { joinThread } from "../../shared/_helpers/socket";
import {
  getThreadMessages,
  markThreadRead,
} from "../../shared/_services/thread.services";
import {
  filterChanged,
  lastReplyOf,
  loadCounts,
  loadInbox,
  openMessageReceived,
  openMessagesLoaded,
  refreshOpenRow,
  rowMarkedRead,
  selectInboxFilter,
  selectInboxStatus,
  selectOpen,
  selectOpenClient,
  summaryLastReplyPatched,
} from "../../redux/inboxSlice";
import InboxList from "./InboxList";
import InboxEmptyColumn from "./InboxEmptyColumn";
import AdvisorThread from "./AdvisorThread";
import ClientSidebar from "./ClientSidebar";

// One list refetch per burst of `thread updated` events (spec § Socket).
export const LIST_REFETCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;

// The advisor's inbox (SP2 spec § Web client): list · thread · client record,
// 320px · 1fr · 320px (below 1024px the record drops under the thread). Loads the list on mount and whenever the chip changes;
// the selected row and the open thread come from :threadId, and the record
// column follows the open thread's client once the thread has loaded.
//
// Live: every staff reader's socket is in `advisors:inbox`, which carries
// `thread updated` after any saved message, first-reply claim or take-over;
// the open thread's own room (joined by AdvisorThread) carries
// `message received`. Sends never go over the socket.
export default function InboxPage() {
  const dispatch = useDispatch();
  const store = useStore();
  const { threadId } = useParams();
  const filter = useSelector(selectInboxFilter);
  const status = useSelector(selectInboxStatus);
  const open = useSelector(selectOpen);
  const client = useSelector(selectOpenClient);
  const clientId =
    threadId && open.threadId === threadId
      ? (open.thread?.clientId ?? null)
      : null;

  const checkedFor = useRef(null);
  // The listeners are registered once; they read the open thread from here.
  const openThreadRef = useRef(null);
  openThreadRef.current = threadId ?? null;

  useEffect(() => {
    dispatch(loadInbox());
  }, [dispatch, filter]);

  // R70: a deep link to a thread the current chip does not list switches the
  // chip to All. Checked once per :threadId, on the store's state after a
  // completed load (not this render's, which can predate the load just
  // dispatched), so a chip the advisor picks afterwards is left alone.
  useEffect(() => {
    if (!threadId) {
      checkedFor.current = null;
      return;
    }
    const inbox = store.getState().inbox;
    if (inbox.status !== "ready" || checkedFor.current === threadId) return;
    checkedFor.current = threadId;
    const listed = [...inbox.rows, ...inbox.noMessagesYet].some(
      (r) => r.threadId === threadId,
    );
    if (!listed && inbox.filter !== "all") dispatch(filterChanged("all"));
  }, [threadId, status, store, dispatch]);

  // AuthContext opens the socket once the session verifies (SEC-085); this
  // page, rendered only behind that gate, just listens.
  useEffect(() => {
    let timer = null;
    // Set when the burst touched the open thread: its row may have left the
    // chip, so refreshOpenRow reads it from All (R77).
    let openTouched = false;

    const onThreadUpdated = (event) => {
      const current = openThreadRef.current;
      if (current && String(event?.threadId) === current) openTouched = true;
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        dispatch(loadInbox({ quiet: true }));
        if (openTouched) {
          openTouched = false;
          dispatch(refreshOpenRow());
        }
      }, LIST_REFETCH_DEBOUNCE_MS);
    };

    const onMessage = (m) => {
      const current = openThreadRef.current;
      if (!current || String(m.threadId) !== current) return;
      dispatch(openMessageReceived(m));
      // An advisor's reply is the cached Summary's new last reply (R79).
      const lastReply = lastReplyOf(m);
      if (lastReply)
        dispatch(
          summaryLastReplyPatched({
            clientId: store.getState().inbox.open.thread?.clientId ?? null,
            lastReply,
          }),
        );
      if (m.senderType === "client")
        markThreadRead(current)
          .then(() => {
            dispatch(rowMarkedRead(current));
            dispatch(loadCounts({ force: true }));
          })
          .catch(() => {});
    };

    // Runs on every connection, including the one after a drop: whatever
    // was missed while the socket was down is fetched again over HTTP.
    const onConnect = async () => {
      dispatch(loadInbox({ quiet: true }));
      const current = openThreadRef.current;
      if (!current) return;
      await joinThread(current);
      try {
        const page = await getThreadMessages(current, { limit: PAGE_SIZE });
        dispatch(
          openMessagesLoaded({
            threadId: current,
            messages: page.data.messages,
            nextBefore: page.data.nextBefore,
          }),
        );
      } catch (_) {
        /* the next open of the thread fetches it again */
      }
    };

    socket.on("thread updated", onThreadUpdated);
    socket.on("message received", onMessage);
    socket.on("connect", onConnect);
    return () => {
      clearTimeout(timer);
      socket.off("thread updated", onThreadUpdated);
      socket.off("message received", onMessage);
      socket.off("connect", onConnect);
    };
  }, [dispatch, store]);

  return (
    <div
      data-slot="inbox"
      className="tw-flex tw-h-screen tw-overflow-hidden tw-bg-bg max-lg:tw-h-auto max-lg:tw-flex-wrap max-rail:tw-flex-col"
    >
      <InboxList selectedThreadId={threadId ?? null} />
      {threadId ? (
        <AdvisorThread key={threadId} threadId={threadId} />
      ) : (
        <InboxEmptyColumn kind="thread" />
      )}
      {clientId ? (
        <ClientSidebar key={clientId} clientId={clientId} client={client} />
      ) : (
        <InboxEmptyColumn kind="side" />
      )}
    </div>
  );
}
